import { privateRequest, type ApiResponse } from "../axios";

const ENDPOINTS = {
  LEAVE_SETTING: "api/GetLeaveSetting",
  UPDATE_LEAVE_SETTING: "api/SetLeaveSetting",
  LEAVE_TYPE: "api/Leave/LeaveType",
  GET_LEAVE_USER: "api/GetLeaveUser",
  DEFAULT_APPROVERS: "api/DefaultApprovers",
  DEFAULT_DELETE_APPROVER: "api/DefaultApprover",
  REVIEWERS: "api/Reviewers",
  DELETE_REVIEWERS: "api/Reviewer",
  DELETE_LEAVE_USER: "api/DeleteLeaveUser",
  GET_TEAM_MEMBERS: "api/GetTeamMembers",
  ADD_LEAVE_USERS: "api/AddLeaveUser",
  COMPANY_MEMBER: "api/CompanyMembers",
  APPROVERS: "api/Approvers",
  APPROVER: "api/Approver",
};

const getLeaveSetting = async (): Promise<ApiResponse<any>> => {
  const res = await privateRequest.get<ApiResponse<any>>(ENDPOINTS.LEAVE_SETTING);
  return res.data;
};

const updateLeaveSetting = async (payload: any): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.UPDATE_LEAVE_SETTING, payload);
  return res.data;
};

const getLeaveTypes = async (): Promise<ApiResponse<any[]>> => {
  const res = await privateRequest.get<ApiResponse<any[]>>(ENDPOINTS.LEAVE_TYPE);
  return res.data;
};

const addLeaveType = async (payload: any): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.LEAVE_TYPE, payload);
  return res.data;
};

const deleteLeaveType = async (id: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.delete<ApiResponse<any>>(`${ENDPOINTS.LEAVE_TYPE}/${id}`);
  return res.data;
};

const updateLeaveType = async (payload: { Id: number; Type: string; Meta: { FontColor: string; BackgroundColor: string; DisplayName: string } }): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(ENDPOINTS.LEAVE_TYPE, payload);
  return res.data;
};


const getLeaveUsers = async (): Promise<ApiResponse<any[]>> => {
  const res = await privateRequest.get<ApiResponse<any[]>>(ENDPOINTS.GET_LEAVE_USER);
  return res.data;
};

const addLeaveUser = async (userId: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.GET_LEAVE_USER, { UserId: userId });
  return res.data;
};

const deleteLeaveUser = async (userId: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.delete<ApiResponse<any>>(`${ENDPOINTS.DELETE_LEAVE_USER}?userId=${userId}`);
  return res.data;
};

const getDefaultApprovers = async (): Promise<ApiResponse<any[]>> => {
  const res = await privateRequest.get<ApiResponse<any[]>>(ENDPOINTS.DEFAULT_APPROVERS);
  return res.data;
};

const addDefaultApprover = async (userId: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.DEFAULT_APPROVERS, { UserId: userId });
  return res.data;
};

const addBulkDefaultApprovers = async (payload: { UserId: number }[]): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.DEFAULT_DELETE_APPROVER, payload);
  return res.data;
};

const deleteDefaultApprover = async (id: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(`${ENDPOINTS.DEFAULT_DELETE_APPROVER}/${id}`);
  return res.data;
};

const getLeaveReviewers = async (): Promise<ApiResponse<any[]>> => {
  const res = await privateRequest.get<ApiResponse<any[]>>(ENDPOINTS.REVIEWERS);
  return res.data;
};

const addLeaveReviewer = async (userId: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.REVIEWERS, { ReviewerUserId: userId });
  return res.data;
};

const addBulkLeaveReviewers = async (payload: { UserId: number }[]): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.DELETE_REVIEWERS, payload);
  return res.data;
};

const deleteLeaveReviewer = async (ReviewerUserId: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(`${ENDPOINTS.DELETE_REVIEWERS}/${ReviewerUserId}`);
  return res.data;
};

const getTeamMembers = async (): Promise<ApiResponse<any[]>> => {
  const res = await privateRequest.get<ApiResponse<any[]>>(ENDPOINTS.GET_TEAM_MEMBERS);
  return res.data;
};

const addLeaveUsers = async (userIds: string): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(`${ENDPOINTS.ADD_LEAVE_USERS}?userIds=${userIds}`);
  return res.data;
};

const getCompanyMembers = async (data: any): Promise<ApiResponse<any>> => {
  const res = await privateRequest.get<ApiResponse<any>>(
    `${ENDPOINTS.COMPANY_MEMBER}?From=${data.from}&To=${data.to}&text=${data.text}`
  );
  return res.data;
};

const getApprovers = async (data: any): Promise<ApiResponse<any>> => {
  const res = await privateRequest.get<ApiResponse<any>>(
    `${ENDPOINTS.APPROVERS}?From=${data.from}&To=${data.to}${data.UserName ? `&UserName=${data.UserName}` : ''}${data.ManagerIds ? `&ManagerIds=${data.ManagerIds}` : ''}`
  );
  return res.data;
};

const setApprovers = async (payload: any): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.APPROVERS, payload);
  return res.data;
};

const deleteApprover = async (leaveUserId: number, approverId: number, approvalOrder: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(
    `${ENDPOINTS.APPROVER}?leaveUserId=${leaveUserId}&approverId=${approverId}&approvalOrder=${approvalOrder}`
  );
  return res.data;
};

const LeaveSettingsService = {
  getLeaveSetting,
  updateLeaveSetting,
  getLeaveTypes,
  addLeaveType,
  deleteLeaveType,
  updateLeaveType,
  getLeaveUsers,
  addLeaveUser,
  deleteLeaveUser,
  getDefaultApprovers,
  addDefaultApprover,
  deleteDefaultApprover,
  addBulkDefaultApprovers,
  getLeaveReviewers,
  addLeaveReviewer,
  deleteLeaveReviewer,
  addBulkLeaveReviewers,
  getTeamMembers,
  addLeaveUsers,
  getCompanyMembers,
  getApprovers,
  setApprovers,
  deleteApprover,
};

export default LeaveSettingsService;
